import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Gate from '../components/access/Gate';

// Long enough to read the farewell, short enough not to feel stuck. The
// "Return to the gate" link is always there for anyone who'd rather not wait.
const REDIRECT_MS = 3200;

/**
 * The closing half of the door metaphor: /login shows the gate from outside,
 * the chat is the hall within, and this is the door sealing behind you.
 *
 * It is a plain (unguarded) route on purpose. Signing out flips the user to
 * false, which would bounce us straight to /login from any guarded route
 * before the farewell ever rendered.
 */
const Farewell = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const signedOut = useRef(false);

  useEffect(() => {
    // StrictMode double-invokes effects in dev; only ever sign out once.
    if (signedOut.current) return;
    signedOut.current = true;
    logout();
  }, [logout]);

  useEffect(() => {
    const timer = setTimeout(() => navigate('/login', { replace: true }), REDIRECT_MS);
    return () => clearTimeout(timer);
  }, [navigate]);

  return (
    <Gate anchor="seal" data-testid="farewell-screen">
      {/* Standing where the form stood a moment ago, on the unchanged gate:
          the door is the same one, now closed behind you. */}
      <div className="text-center pt-2 farewell-enter">
        <h2
          className="font-heading text-2xl tracking-[0.14em] uppercase"
          style={{ color: 'hsl(var(--gold-pale))' }}
        >
          Until next time
        </h2>
        <div className="mx-auto my-3 h-px w-40" style={{ background: 'hsl(var(--gold) / 0.35)' }} aria-hidden="true" />
        <p className="text-sm text-foreground/85 mb-6">The door is sealed behind you.</p>
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          className="min-h-[44px] px-4 text-xs font-semibold uppercase tracking-[0.14em] transition-colors hover:text-foreground"
          style={{ color: 'hsl(var(--gold))' }}
          data-testid="farewell-return"
        >
          Return to the gate
        </button>
      </div>
    </Gate>
  );
};

export default Farewell;
